import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { money, fmtDate, daysUntil } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, CheckCircle2, ArrowUpRight, ArrowDownRight, Clock, Link2, AlertTriangle, Coins, Search, Building2, Check, X, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fmtD = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
};

const TABS = [
  ["inflows", "Banka Girişleri"],
  ["unmatched", "Eşleşmeyen"],
  ["receivables", "Alacaklar"],
  ["upcoming", "Yaklaşan"],
];

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [inflows, setInflows] = useState([]);
  const [colSum, setColSum] = useState({});
  const [tab, setTab] = useState("inflows");
  const [upTab, setUpTab] = useState("pending");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "payment", title: "", amount: "", due_date: new Date().toISOString().slice(0, 10), contact_id: "", account_id: "" });

  // assign dialog state
  const [assignTx, setAssignTx] = useState(null);
  const [assignQuery, setAssignQuery] = useState("");
  const [remember, setRemember] = useState(true);

  const load = async () => {
    const [p, c, a, inf, cs] = await Promise.all([
      api.payments(), api.contacts(), api.accounts(),
      api.bankInflows().catch(() => ({ items: [] })),
      api.collectionsSummary().catch(() => ({})),
    ]);
    setPayments(p);
    setContacts(c);
    setAccounts(a);
    setInflows(inf.items || []);
    setColSum(cs || {});
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!form.title || !form.amount) {
      toast.error("Başlık ve tutar zorunludur");
      return;
    }
    await api.createPayment({
      ...form,
      amount: parseFloat(form.amount),
      due_date: new Date(form.due_date).toISOString(),
      contact_id: form.contact_id || null,
      account_id: form.account_id || null,
    });
    toast.success("Kayıt eklendi");
    setOpen(false);
    setForm({ type: "payment", title: "", amount: "", due_date: new Date().toISOString().slice(0, 10), contact_id: "", account_id: "" });
    load();
  };

  const markPaid = async (id) => {
    await api.markPaid(id);
    toast.success("İşlem tamamlandı olarak işaretlendi");
    load();
  };
  const remove = async (id) => {
    await api.deletePayment(id);
    toast.success("Kayıt silindi");
    load();
  };

  const doAssign = async (contactId) => {
    try {
      const r = await api.assignBankContact(assignTx.id, { contact_id: contactId, remember });
      toast.success(`"${r.contact_name}" cari olarak atandı${r.covered ? " (ekstrede mevcut)" : ""}${r.capped ? " · avans olarak işaretlendi" : ""}`);
      setAssignTx(null);
      setAssignQuery("");
      load();
    } catch {
      toast.error("Atama başarısız");
    }
  };

  const doConfirmSuggestion = async (it) => {
    try {
      const r = await api.confirmSuggestion(it.id);
      toast.success(`Onaylandı — "${r.contact_name}" cari olarak atandı ve bu isim için otomatik eşleştirme kuralı kaydedildi`);
      load();
    } catch {
      toast.error("Onaylama başarısız");
    }
  };

  const doRejectSuggestion = async (it) => {
    try {
      await api.rejectSuggestion(it.id);
      toast.success("Öneri reddedildi — hareket eşleşmeyen olarak kaldı");
      load();
    } catch {
      toast.error("İşlem başarısız");
    }
  };

  const [reprocessing, setReprocessing] = useState(false);
  const doReprocess = async () => {
    setReprocessing(true);
    try {
      const r = await api.reprocessUnmatched();
      toast.success(`Yeniden tarama tamam: ${r.applied} hareket cariye bağlandı · ${r.suggested} olası eşleşme onaya düştü`);
      await load();
    } catch {
      toast.error("Yeniden tarama başarısız");
    } finally {
      setReprocessing(false);
    }
  };

  const unmatched = useMemo(
    () => inflows.filter((i) => !i.contact_id).sort((a, b) => (b.suggested_contact_id ? 1 : 0) - (a.suggested_contact_id ? 1 : 0)),
    [inflows]
  );
  const suggestionCount = useMemo(() => unmatched.filter((i) => i.suggested_contact_id).length, [unmatched]);
  const customers = useMemo(() => contacts.filter((c) => c.type === "customer"), [contacts]);
  const receivables = useMemo(() => customers.filter((c) => (c.balance || 0) > 0.01).sort((a, b) => b.balance - a.balance), [customers]);
  const collected = useMemo(() => customers.filter((c) => (c.balance || 0) <= 0.01 && colSum[c.id]).sort((a, b) => (colSum[b.id]?.last_date || "").localeCompare(colSum[a.id]?.last_date || "")), [customers, colSum]);

  const assignChoices = useMemo(() => {
    const q = assignQuery.trim().toLowerCase();
    let list = contacts;
    if (q) list = contacts.filter((c) => c.name.toLowerCase().includes(q) || (c.tax_number || "").toLowerCase().includes(q));
    return list.slice(0, 40);
  }, [contacts, assignQuery]);

  const upPayments = payments.filter((p) => p.status === upTab);

  return (
    <div className="p-5 pt-20 lg:p-10 lg:pt-10">
      <PageHeader
        title="Ödeme / Tahsilat"
        subtitle="Banka para girişleri, eşleşmeyen tahsilatlar ve müşteri alacakları"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-payment-button" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="h-4 w-4" /> <span className="ml-1.5">Yeni Kayıt</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card">
              <DialogHeader><DialogTitle className="font-display">Yeni Ödeme / Tahsilat</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="payment-type-payment" onClick={() => setForm({ ...form, type: "payment" })} className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${form.type === "payment" ? "border-rose-500 bg-rose-500/15 text-rose-400" : "border-border text-muted-foreground"}`}>Ödeme (çıkış)</button>
                  <button data-testid="payment-type-collection" onClick={() => setForm({ ...form, type: "collection" })} className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${form.type === "collection" ? "border-emerald-500 bg-emerald-500/15 text-emerald-400" : "border-border text-muted-foreground"}`}>Tahsilat (giriş)</button>
                </div>
                <div><Label>Başlık</Label><Input data-testid="payment-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="Ofis kirası" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Tutar (lei)</Label><Input data-testid="payment-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>Vade Tarihi</Label><Input data-testid="payment-date-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" /></div>
                </div>
                <div>
                  <Label>Cari Hesap</Label>
                  <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Opsiyonel" /></SelectTrigger>
                    <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Banka Hesabı</Label>
                  <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Opsiyonel" /></SelectTrigger>
                    <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="payment-submit-button" onClick={submit} className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map(([k, l]) => {
          const badge = k === "unmatched" ? unmatched.length : k === "receivables" ? receivables.length : null;
          return (
            <button key={k} data-testid={`payment-tab-${k}`} onClick={() => setTab(k)} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {l}
              {badge > 0 && <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${k === "unmatched" ? "bg-amber-500/25 text-amber-200" : "bg-rose-500/25 text-rose-200"}`}>{badge}</span>}
            </button>
          );
        })}
      </div>

      {/* Banka Girişleri + Eşleşmeyen */}
      {(tab === "inflows" || tab === "unmatched") && (
        <div className="space-y-2">
          {tab === "unmatched" && (
            <div className="mb-1 flex items-center justify-between">
              <div className="text-xs text-muted-foreground">Eşleşmeyen banka girişlerini yeniden tara: Dante/HeyBlue online ödemeleri "Online Satışlar"a bağlanır, benzer isimler onaya düşer.</div>
              <Button data-testid="reprocess-unmatched-button" onClick={doReprocess} disabled={reprocessing} size="sm" variant="outline" className="shrink-0 border-primary/40 text-primary hover:bg-primary/10">
                <RefreshCw className={`h-4 w-4 ${reprocessing ? "animate-spin" : ""}`} /> <span className="ml-1.5">{reprocessing ? "Taranıyor…" : "Yeniden Tara"}</span>
              </Button>
            </div>
          )}
          {tab === "unmatched" && suggestionCount > 0 && (
            <div data-testid="suggestion-banner" className="mb-3 flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/[0.06] px-4 py-3 text-sm text-primary">
              <Sparkles className="h-4 w-4 shrink-0" />
              <span><strong>{suggestionCount}</strong> olası (otomatik) eşleşme onayınızı bekliyor. Doğru olanları "Onayla", yanlış olanları "Reddet" ile işaretleyin.</span>
            </div>
          )}
          {(tab === "inflows" ? inflows : unmatched).length === 0 && (
            <div className="py-12 text-center text-muted-foreground">{tab === "unmatched" ? "Eşleşmeyen tahsilat yok — hepsi bir cariye bağlı 🎉" : "Banka girişi bulunamadı"}</div>
          )}
          {(tab === "inflows" ? inflows : unmatched).map((it) => (
            <div key={it.id} data-testid="inflow-item" className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:border-emerald-500/30 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3.5 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400"><ArrowDownRight className="h-5 w-5" /></div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {it.contact_id ? (
                      <span data-testid="inflow-matched" className="truncate font-medium text-emerald-300">{it.contact_name}</span>
                    ) : (
                      <span className="truncate font-medium">{it.counterparty || "Bilinmeyen gönderen"}</span>
                    )}
                    {it.contact_id ? (
                      <span className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-400">Eşleşti</span>
                    ) : it.suggested_contact_id ? (
                      <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary"><Sparkles className="h-2.5 w-2.5" /> Öneri</span>
                    ) : (
                      <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-300"><AlertTriangle className="h-2.5 w-2.5" /> Eşleşmedi</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" /> {fmtD(it.date)}
                    {it.contact_id && it.counterparty && <span className="truncate">· {it.counterparty}</span>}
                  </div>
                  {!it.contact_id && it.suggested_contact_id && (
                    <div className="mt-0.5 flex items-center gap-1 text-xs text-primary sm:hidden"><Sparkles className="h-3 w-3" /> Öneri: {it.suggested_contact_name}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 sm:justify-end">
                <div className="font-mono text-base font-semibold tabular text-emerald-400">+{money(it.amount)}{it.currency !== "RON" ? ` ${it.currency}` : ""}</div>
                {!it.contact_id && it.suggested_contact_id && (
                  <div className="flex items-center gap-1.5">
                    <span className="hidden items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary lg:inline-flex"><Sparkles className="h-3 w-3" /> {it.suggested_contact_name}</span>
                    <Button data-testid="confirm-suggestion-button" onClick={() => doConfirmSuggestion(it)} size="sm" className="bg-emerald-600 text-white hover:bg-emerald-500"><Check className="h-4 w-4" /><span className="ml-1 hidden sm:inline">Onayla</span></Button>
                    <Button data-testid="reject-suggestion-button" onClick={() => doRejectSuggestion(it)} size="sm" variant="outline" className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10"><X className="h-4 w-4" /></Button>
                    <Button data-testid="assign-contact-button" onClick={() => { setAssignTx(it); setAssignQuery(it.counterparty || ""); setRemember(true); }} size="sm" variant="ghost" className="text-muted-foreground hover:text-foreground"><Link2 className="h-4 w-4" /></Button>
                  </div>
                )}
                {!it.contact_id && !it.suggested_contact_id && (
                  <Button data-testid="assign-contact-button" onClick={() => { setAssignTx(it); setAssignQuery(it.counterparty || ""); setRemember(true); }} size="sm" variant="outline" className="border-primary/40 text-primary hover:bg-primary/10">
                    <Link2 className="h-4 w-4" /> <span className="ml-1 hidden sm:inline">Cari Ata</span>
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Alacaklar (Ödenmemiş) + Tahsil edildi */}
      {tab === "receivables" && (
        <div className="space-y-5">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-rose-300"><AlertTriangle className="h-4 w-4" /> Ödenmemiş Alacaklar ({receivables.length})</div>
            <div className="space-y-2">
              {receivables.length === 0 && <div className="rounded-lg border border-border bg-card py-6 text-center text-sm text-muted-foreground">Ödenmemiş alacak yok</div>}
              {receivables.map((c) => (
                <div key={c.id} data-testid="receivable-item" className="flex items-center justify-between rounded-xl border border-rose-500/20 bg-rose-500/[0.04] p-4">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-500/15 text-rose-400"><Building2 className="h-5 w-5" /></div>
                    <div className="min-w-0">
                      <div className="truncate font-medium">{c.name}</div>
                      <div className="text-xs text-amber-300/90">Ödenmemiş bakiye{colSum[c.id] ? ` · son tahsilat ${fmtD(colSum[c.id].last_date)}` : " · henüz tahsilat yok"}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-base font-semibold tabular text-rose-400">{money(c.balance)}</div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Alacak</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-300"><CheckCircle2 className="h-4 w-4" /> Tahsil Edildi / Kapandı ({collected.length})</div>
            <div className="space-y-2">
              {collected.length === 0 && <div className="rounded-lg border border-border bg-card py-6 text-center text-sm text-muted-foreground">Kayıt yok</div>}
              {collected.map((c) => (
                <div key={c.id} data-testid="collected-item" className="flex items-center justify-between rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400"><Coins className="h-5 w-5" /></div>
                    <div className="min-w-0">
                      <div className="truncate font-medium">{c.name}</div>
                      <div className="text-xs text-emerald-300/90">Tahsil edildi · son {fmtD(colSum[c.id].last_date)} · {money(colSum[c.id].last_amount)}</div>
                    </div>
                  </div>
                  <div className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-300">Ödendi</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Yaklaşan (manuel) */}
      {tab === "upcoming" && (
        <>
          <div className="mb-4 flex gap-2">
            {[["pending", "Bekleyen"], ["paid", "Tamamlanan"]].map(([k, l]) => (
              <button key={k} data-testid={`payment-uptab-${k}`} onClick={() => setUpTab(k)} className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${upTab === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
          <div className="space-y-3">
            {upPayments.length === 0 && <div className="py-12 text-center text-muted-foreground">Kayıt bulunamadı</div>}
            {upPayments.map((p) => {
              const d = daysUntil(p.due_date);
              const overdue = p.status === "pending" && d < 0;
              const soon = p.status === "pending" && d >= 0 && d <= 3;
              const isColl = p.type === "collection";
              const contact = contacts.find((c) => c.id === p.contact_id);
              return (
                <div key={p.id} data-testid="payment-item" className="group flex flex-col gap-3 rounded-xl border border-border bg-card p-5 transition-transform duration-200 hover:-translate-y-0.5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${isColl ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
                      {isColl ? <ArrowDownRight className="h-5 w-5" /> : <ArrowUpRight className="h-5 w-5" />}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-medium">{p.title}</div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" /> {fmtDate(p.due_date)}
                        {contact && <span>· {contact.name}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4 sm:justify-end">
                    <div className="text-right">
                      <div className={`font-mono text-base font-semibold tabular ${isColl ? "text-emerald-400" : "text-rose-400"}`}>{isColl ? "+" : "-"}{money(p.amount)}</div>
                      {p.status === "pending" && (
                        <div className={`text-[11px] font-medium ${overdue ? "text-rose-400" : soon ? "text-amber-400" : "text-muted-foreground"}`}>
                          {overdue ? `${Math.abs(d)} gün gecikti` : d === 0 ? "Bugün" : `${d} gün kaldı`}
                        </div>
                      )}
                      {p.status === "paid" && <div className="text-[11px] font-medium text-emerald-400">Tamamlandı</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      {p.status === "pending" && (
                        <Button data-testid="mark-paid-button" onClick={() => markPaid(p.id)} size="sm" variant="outline" className="border-primary/40 text-primary hover:bg-primary/10">
                          <CheckCircle2 className="h-4 w-4" /> <span className="ml-1 hidden sm:inline">Tamamlandı</span>
                        </Button>
                      )}
                      <button data-testid="delete-payment-button" onClick={() => remove(p.id)} className="text-muted-foreground transition-colors hover:text-rose-400">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Assign contact dialog */}
      <Dialog open={!!assignTx} onOpenChange={(v) => { if (!v) setAssignTx(null); }}>
        <DialogContent className="bg-card max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Cari Ata</DialogTitle>
            <DialogDescription>
              {assignTx && <>{fmtD(assignTx.date)} · <span className="text-emerald-300 font-mono">+{money(assignTx.amount)}</span> · Gönderen: {assignTx.counterparty || "—"}</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="assign-search-input" autoFocus value={assignQuery} onChange={(e) => setAssignQuery(e.target.value)} placeholder="Cari adı veya vergi no ara…" className="pl-9" />
            </div>
            <div className="max-h-[40vh] space-y-1 overflow-y-auto pr-1">
              {assignChoices.length === 0 && <div className="py-4 text-center text-sm text-muted-foreground">Sonuç yok</div>}
              {assignChoices.map((c) => (
                <button key={c.id} data-testid="assign-contact-choice" onClick={() => doAssign(c.id)} className="flex w-full items-center justify-between rounded-lg border border-border bg-secondary/20 px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-primary/5">
                  <span className="truncate text-sm">{c.name}</span>
                  <span className={`ml-2 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.type === "customer" ? "bg-primary/15 text-primary" : "bg-blue-500/15 text-blue-400"}`}>{c.type === "customer" ? "Müşteri" : "Tedarikçi"}</span>
                </button>
              ))}
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
              <input data-testid="assign-remember-checkbox" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-3.5 w-3.5 accent-emerald-500" />
              Bu isimdeki gelecek girişleri de otomatik eşleştir
            </label>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
